/* RechargeLimitsEditor — admin-editable floor/ceiling for top-up amounts.
 *
 * Lives at: Admin → Settings → Payments & Bonuses → Recharge limits.
 * Backed by: `app_config.recharge_limits` (singleton). The same values
 * are read by `routes/paypal.py` and `routes/stripe_payment.py` on every
 * top-up attempt — both endpoints reject amounts outside this range
 * with a 400, so this card is the single source of truth.
 *
 * UX rules:
 *   • min ≤ max enforced both client- and server-side.
 *   • Floats allowed (e.g. €5.50) — server casts to float and the
 *     formatting helper uses `:g` to drop trailing zeros.
 *   • Empty fields are treated as "unchanged" by the API and don't
 *     overwrite the saved value (so an admin can edit just `min`
 *     without re-typing `max`).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Save, Loader2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RechargeLimitsEditor({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [minR, setMinR] = useState('');
  const [maxR, setMaxR] = useState('');
  const [savedMin, setSavedMin] = useState(null);
  const [savedMax, setSavedMax] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/service/recharge-limits`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setMinR(String(j.min_recharge ?? ''));
      setMaxR(String(j.max_recharge ?? ''));
      setSavedMin(j.min_recharge);
      setSavedMax(j.max_recharge);
    } catch (e) {
      toast.error(`Failed to load recharge limits: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    const body = {};
    const minNum = Number(minR);
    const maxNum = Number(maxR);
    if (minR !== '' && !Number.isFinite(minNum)) { toast.error('Minimum must be a number'); return; }
    if (maxR !== '' && !Number.isFinite(maxNum)) { toast.error('Maximum must be a number'); return; }
    if (minR !== '') body.min_recharge = minNum;
    if (maxR !== '') body.max_recharge = maxNum;
    if (Number.isFinite(minNum) && Number.isFinite(maxNum) && minNum > maxNum) {
      toast.error('Minimum must be ≤ maximum'); return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/service/recharge-limits`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const res = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(typeof res.detail === 'string' ? res.detail : `Save failed (HTTP ${r.status})`);
        return;
      }
      toast.success('Recharge limits updated');
      setSavedMin(res.min_recharge); setSavedMax(res.max_recharge);
      setMinR(String(res.min_recharge)); setMaxR(String(res.max_recharge));
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-8" data-testid="recharge-limits-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const dirty = (
    (minR !== '' && Number(minR) !== savedMin) ||
    (maxR !== '' && Number(maxR) !== savedMax)
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="recharge-limits-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center border border-cyan-100">
          <CreditCard className="w-5 h-5 text-cyan-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-800">Recharge limits</h3>
          <p className="text-[12px] text-slate-500 mt-0.5">
            Applies to every top-up — PayPal and Stripe both read these values
            on each <code>/recharge</code> request. Used to filter out
            sub-cost-of-processing payments. Changes take effect instantly,
            no redeploy needed.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">
            Minimum amount (€)
          </label>
          <input
            type="number" min={0} step="0.01"
            value={minR}
            onChange={(e) => setMinR(e.target.value)}
            data-testid="recharge-limits-min-input"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm tabular-nums"
            placeholder={String(savedMin ?? 5)}
          />
          <div className="text-[11px] text-slate-400 mt-1">
            Saved: <span className="font-mono">€{savedMin ?? '—'}</span>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">
            Maximum amount (€)
          </label>
          <input
            type="number" min={0} step="0.01"
            value={maxR}
            onChange={(e) => setMaxR(e.target.value)}
            data-testid="recharge-limits-max-input"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm tabular-nums"
            placeholder={String(savedMax ?? 10000)}
          />
          <div className="text-[11px] text-slate-400 mt-1">
            Saved: <span className="font-mono">€{savedMax?.toLocaleString?.() ?? '—'}</span>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
        <Info className="w-4 h-4 text-slate-400 mt-px shrink-0" />
        <div>
          PayPal charges a fixed €0.35 + 2.9% per transaction; €5 is the
          rough break-even for European/USA cards. Going below that means
          you net less than €4.50 after fees. The customer-facing wallet
          page reads these values from the same endpoint, so the visible
          "Minimum: €X" badge updates instantly after you save here.
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving || !dirty}
          data-testid="recharge-limits-save-btn"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
